/**
 * One repository's mini dashboard, on the By repository tab (DEC-191 decision 1).
 *
 * The source and the last status, four key numbers, a history chart with one bar per
 * scan (height = findings split by severity, how it ended written under every bar, so
 * colour is never the only signal), then every scan of it as rows. The chart is the full
 * history whatever the filters, and says so. One scan is not a history yet.
 *
 * Uploaded folders group by name, which is all TDIA can do: it does not keep them, so it
 * cannot check two uploads hold the same code, and the page says that too.
 */
import { useState } from 'react'
import type { ScanStatusResponse } from '../../types/api'
import { isUploadScan } from '../../utils/format'
import { ActionButton, type DashItem, type RowAction, ScanRow, StatusDot, StatusPill } from './ScanRow'
import {
  SEVERITIES,
  SEVERITY_MARK,
  SEVERITY_WORD,
  agoLabel,
  dayLabel,
  hm,
  repoLabel,
  scanSentence,
  sevLine,
  sevTotal,
  sourceLabel,
  whenLabel,
} from './scanFormat'
import { SCAN_STATUS, statusOf, type ScanStatusKey } from './scanStatus'

const CHART_BARS = 12
const CHART_H = 160

function niceMax(m: number): number {
  if (m <= 10) return 10
  const p = Math.pow(10, Math.floor(Math.log10(m)))
  for (const step of [1, 2, 2.5, 5, 10]) if (step * p >= m) return step * p
  return 10 * p
}

function KeyNumber({ label, value, sub, muted = false }: { label: string; value: string; sub: string; muted?: boolean }) {
  return (
    <div className="flex flex-col gap-[6px] border-hairline px-4 py-[14px] [&:not(:first-child)]:border-l">
      <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">{label}</span>
      <span className={`tabular-nums text-[26px] font-medium leading-[1.15] ${muted ? 'text-muted' : 'text-ink'}`}>{value}</span>
      <span className="text-[12px] leading-[1.45] text-body">{sub}</span>
    </div>
  )
}

export interface RepoDashboardProps {
  repoUrl: string
  /** Every scan of this repository, newest first, unfiltered. */
  history: ScanStatusResponse[]
  /** How many there are in all, when more exist than were loaded. */
  totalInHistory: number
  loading: boolean
  scanActions: (scan: ScanStatusResponse, status: ScanStatusKey) => RowAction[]
  againAction: (scan: ScanStatusResponse) => RowAction
  openResults: (scan: ScanStatusResponse) => void
  now?: number
}

export function RepoDashboard({
  repoUrl,
  history,
  totalInHistory,
  loading,
  scanActions,
  againAction,
  openResults,
  now = Date.now(),
}: RepoDashboardProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const isUpload = isUploadScan(repoUrl)
  const noun = isUpload ? 'folder' : 'repository'
  const name = repoLabel(repoUrl)

  if (loading && history.length === 0) {
    return <div className="rounded-lg border border-hairline bg-panel p-6 text-[13px] text-muted shadow-card">Loading its history...</div>
  }
  if (history.length === 0) {
    return <div className="rounded-lg border border-hairline bg-panel p-6 text-[13px] text-muted shadow-card">No scans of this {noun} are left.</div>
  }

  const withStatus = history.map((s) => ({ scan: s, status: statusOf(s) }))
  const latest = withStatus[0]
  const ended = withStatus.filter((x) => x.status === 'complete' || x.status === 'partial' || x.status === 'failed')
  const full = withStatus.filter((x) => x.status === 'complete')
  const gapsN = withStatus.filter((x) => x.status === 'partial').length
  const failN = withStatus.filter((x) => x.status === 'failed').length
  const stopN = withStatus.filter((x) => x.status === 'stopped').length
  const lastFinished = withStatus.find((x) => x.status === 'complete' || x.status === 'partial')
  const first = withStatus[withStatus.length - 1]

  // Key numbers
  const firstDay = dayLabel(first.scan.started_at, now)
  const scansSub =
    history.length === 1
      ? `Only one so far (${whenLabel(first.scan.started_at, now)}).`
      : `Since ${firstDay}${firstDay === 'Today' || firstDay === 'Yesterday' ? '' : `, ${agoLabel(first.scan.started_at, now)}`}.`
  let rate: { value: string; sub: string; muted: boolean }
  if (ended.length >= 2) {
    const bits = [gapsN ? `${gapsN} with gaps` : '', failN ? `${failN} failed` : ''].filter(Boolean)
    rate = {
      value: `${Math.round((full.length / ended.length) * 100)}%`,
      sub: `${full.length} of ${ended.length} finished fully${bits.length ? `, ${bits.join(', ')}` : ''}.${stopN ? ' Scans you stopped are not counted.' : ''}`,
      muted: false,
    }
  } else {
    rate = { value: 'Not yet', sub: 'Needs at least 2 scans that ran to the end.', muted: true }
  }
  const last = lastFinished
    ? {
        value: dayLabel(lastFinished.scan.completed_at ?? lastFinished.scan.started_at, now),
        sub: `${hm(lastFinished.scan.completed_at ?? lastFinished.scan.started_at)} · ${sevTotal(lastFinished.scan.findings_by_severity)} findings${lastFinished.status === 'partial' ? ', with gaps' : ''}.`,
        muted: false,
      }
    : { value: 'None yet', sub: `No scan of this ${noun} has finished.`, muted: true }
  let delta: { value: string; sub: string; muted: boolean }
  if (full.length >= 2) {
    const a = sevTotal(full[0].scan.findings_by_severity)
    const b = sevTotal(full[1].scan.findings_by_severity)
    const d = a - b
    delta = {
      value: d === 0 ? 'No change' : `${Math.abs(d)} ${d < 0 ? 'fewer' : 'more'}`,
      sub: `${a} on ${dayLabel(full[0].scan.started_at, now)}, ${b} on ${dayLabel(full[1].scan.started_at, now)}. Only full scans are compared, so the numbers mean the same thing.`,
      muted: false,
    }
  } else {
    delta = {
      value: 'Not yet',
      sub: full.length === 1 ? 'Only one full scan, so there is nothing to compare it with.' : 'Needs two full scans to compare.',
      muted: true,
    }
  }

  // Chart: oldest left, newest right. Waiting scans have no outcome yet.
  const chartable = withStatus.filter((x) => SCAN_STATUS[x.status].group !== 'waiting')
  const bars = chartable.slice(0, CHART_BARS).reverse()
  const waitingN = withStatus.length - chartable.length
  const isHistory = bars.length >= 2
  const maxT = bars.reduce((m, x) => Math.max(m, sevTotal(x.scan.findings_by_severity)), 0)
  const NM = niceMax(maxT)
  const sel = bars.find((x) => x.scan.scan_id === selectedId) ?? bars[bars.length - 1] ?? null

  const notes = ['Bar height is the number of findings, split by severity. The word under each bar is how the scan ended.']
  if (gapsN) notes.push('A dashed outline means finished with gaps: some areas were not checked, so that count is lower than the real one.')
  if (bars.some((x) => x.status === 'running' || x.status === 'paused')) notes.push('A scan still running has no bar yet; its count arrives when it finishes.')
  if (waitingN) notes.push(`${waitingN} ${waitingN === 1 ? 'scan is' : 'scans are'} waiting to start and will appear when they run.`)
  if (chartable.length > CHART_BARS) notes.push(`Showing the last ${CHART_BARS} of ${chartable.length}. Older ones are in the list below.`)
  if (totalInHistory > history.length) notes.push(`${totalInHistory - history.length} older scans are not loaded here.`)

  const single = bars[0]
  const repoActs: RowAction[] = []
  if (lastFinished) repoActs.push({ label: 'Open latest results', tone: 'primary', onClick: () => openResults(lastFinished.scan) })
  repoActs.push(againAction(latest.scan))

  const rows: DashItem[] = withStatus.map((x) => ({ kind: 'scan', id: x.scan.scan_id, status: x.status, at: x.scan.started_at, scan: x.scan }))

  return (
    <div data-testid="repo-dashboard" className="flex min-w-0 flex-col gap-[14px]">
      <div className="flex flex-col gap-3 rounded-lg border border-hairline bg-panel px-[22px] py-5 shadow-card">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex min-w-0 flex-col gap-[6px]">
            <span className="text-[22px] font-semibold text-ink">{name}</span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-sm border border-hairline-strong px-2 py-[2px] text-[12px] text-body">
                {isUpload ? 'Uploaded folder, grouped by name' : sourceLabel(repoUrl, latest.scan.branch)}
              </span>
              <span className="text-[12px] text-muted">Last scan</span>
              <StatusPill status={latest.status} />
              <span className="text-[12px] text-muted">{whenLabel(latest.scan.started_at, now)} · {agoLabel(latest.scan.started_at, now)}</span>
            </div>
          </div>
          <div className="ml-auto flex flex-wrap gap-2">
            {repoActs.map((a) => <ActionButton key={a.label} action={a} onClick={a.onClick} />)}
          </div>
        </div>
        {isUpload && history.length > 1 && (
          <p data-testid="upload-group-note" className="rounded-md bg-fill-subtle px-3 py-2 text-[12px] leading-[1.5] text-body">
            These {history.length} uploads are shown together because they share the name {name}. TDIA does not keep
            uploaded folders, so it cannot check that they hold the same code.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-hairline bg-panel shadow-card md:grid-cols-4">
        <KeyNumber label="Scans" value={String(history.length)} sub={scansSub} />
        <KeyNumber label="Success rate" {...rate} />
        <KeyNumber label="Last finished" {...last} />
        <KeyNumber label="Findings vs previous" {...delta} />
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-hairline bg-panel px-5 py-[18px] shadow-card">
        <div className="flex flex-wrap items-baseline gap-[10px]">
          <span className="text-[15px] font-semibold text-ink">History</span>
          {isHistory && <span className="text-[13px] text-muted">Each bar is one scan, oldest on the left. Click a bar to see it.</span>}
          <span className="ml-auto flex flex-wrap gap-3 text-[12px] text-muted">
            {SEVERITIES.map((k) => (
              <span key={k} className="inline-flex items-center gap-[5px] whitespace-nowrap">
                <span className={`h-[10px] w-[10px] rounded-[2px] ${SEVERITY_MARK[k]}`} />
                {SEVERITY_WORD[k]}
              </span>
            ))}
          </span>
        </div>

        {isHistory ? (
          <>
            <div className="relative pl-10" data-testid="history-chart">
              {[0, NM / 2, NM].map((v) => (
                <div
                  key={v}
                  className={`absolute left-10 right-0 border-t ${v === 0 ? 'border-hairline' : 'border-dashed border-hairline'}`}
                  style={{ top: 6 + 4 + 18 + (CHART_H - Math.round((v / NM) * CHART_H)) }}
                >
                  <span className="absolute -left-10 -top-2 w-[34px] text-right tabular-nums text-[10px] text-faint">{v}</span>
                </div>
              ))}
              <div className="relative flex items-stretch gap-[6px]">
                {bars.map((x) => {
                  const t = sevTotal(x.scan.findings_by_severity)
                  const S = SCAN_STATUS[x.status]
                  const on = sel?.scan.scan_id === x.scan.scan_id
                  const hasBar = (x.status === 'complete' || x.status === 'partial') && t > 0
                  const mark = x.status === 'failed' ? 'Did not run' : x.status === 'stopped' ? 'Stopped' : x.status === 'running' || x.status === 'paused' ? S.short : 'None'
                  const px = Math.max(4, Math.round((t / NM) * CHART_H))
                  const sev = x.scan.findings_by_severity ?? {}
                  return (
                    <button
                      key={x.scan.scan_id}
                      type="button"
                      data-testid="history-bar"
                      aria-pressed={on}
                      aria-label={`${whenLabel(x.scan.started_at, now)}, ${S.word}${t ? `, ${t} findings: ${sevLine(sev, true)}` : ', no findings'}. Select to see details.`}
                      onClick={() => setSelectedId(x.scan.scan_id)}
                      className={`flex min-w-0 max-w-[92px] flex-1 flex-col items-center gap-1 rounded-md px-[2px] pb-2 pt-[6px] text-ink transition-colors hover:bg-hover-hue ${
                        on ? 'bg-fill-subtle shadow-[inset_0_0_0_1px_rgb(var(--accent))]' : ''
                      }`}
                    >
                      <span className="h-[18px] tabular-nums text-[11px] font-semibold text-ink">{hasBar ? t : ''}</span>
                      <span className="flex w-full flex-col items-center justify-end" style={{ height: CHART_H }}>
                        {hasBar ? (
                          <span
                            className={`flex w-[30px] flex-col-reverse gap-px overflow-hidden rounded-t-[4px] ${
                              x.status === 'partial' ? 'outline-dashed outline-2 outline-offset-2 outline-mark-warn' : ''
                            }`}
                            style={{ height: px }}
                          >
                            {SEVERITIES.filter((k) => (sev[k] ?? 0) > 0).map((k) => (
                              <span key={k} className={`${SEVERITY_MARK[k]} min-h-[2px]`} style={{ flex: `${sev[k]} 1 0` }} />
                            ))}
                          </span>
                        ) : (
                          <span
                            className={`w-[54px] rounded-sm border border-dashed px-[2px] py-[5px] text-center text-[10px] font-semibold leading-[1.2] ${
                              x.status === 'failed' ? 'border-critical text-critical' : 'border-hairline-strong text-muted'
                            }`}
                          >
                            {mark}
                          </span>
                        )}
                      </span>
                      <span className="flex w-full flex-col items-center gap-[2px] border-t border-hairline-strong pt-2">
                        <span className={`inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-semibold ${S.text}`}>
                          <StatusDot status={x.status} size={6} />
                          {S.short}
                        </span>
                        <span className="whitespace-nowrap text-[11px] text-body">{dayLabel(x.scan.started_at, now)}</span>
                        <span className="whitespace-nowrap tabular-nums text-[10px] text-faint">{hm(x.scan.started_at)}</span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
            {sel && (
              <div className="flex flex-wrap items-center gap-[10px] rounded-md bg-fill-subtle px-3 py-[10px] text-[13px] text-body">
                <StatusPill status={sel.status} />
                <span className="min-w-[240px] flex-1">
                  {whenLabel(sel.scan.started_at, now)} ·{' '}
                  {sevTotal(sel.scan.findings_by_severity)
                    ? `${sevTotal(sel.scan.findings_by_severity)} findings: ${sevLine(sel.scan.findings_by_severity, true)}`
                    : 'no findings'}
                  {sel.status === 'complete' ? '' : `. ${scanSentence(sel.status, sel.scan)}`}
                </span>
                {scanActions(sel.scan, sel.status).slice(0, 1).map((a) => (
                  <ActionButton key={a.label} action={a} onClick={a.onClick} />
                ))}
              </div>
            )}
            <p className="text-[12px] leading-[1.5] text-muted">{notes.join(' ')}</p>
          </>
        ) : (
          <div data-testid="history-single" className="flex flex-col gap-[10px] rounded-md border border-dashed border-hairline-strong px-4 py-[14px]">
            <span className="text-[14px] font-semibold text-ink">{single ? 'One scan is not a history yet' : 'Nothing to chart yet'}</span>
            <span className="text-[13px] leading-[1.5] text-body">
              {single
                ? `This ${noun} has been scanned once (${whenLabel(single.scan.started_at, now)}, ${SCAN_STATUS[single.status].word.toLowerCase()}). The chart of how it changes over time starts after the second scan. For now, here is what that one scan found${
                    sevTotal(single.scan.findings_by_severity) ? `: ${sevTotal(single.scan.findings_by_severity)} findings.` : '. It has no findings.'
                  }`
                : `No scan of this ${noun} has run yet. The chart starts once one has.`}
            </span>
            {single && sevTotal(single.scan.findings_by_severity) > 0 && (
              <span className="flex h-10 gap-[2px] overflow-hidden rounded-md">
                {SEVERITIES.filter((k) => (single.scan.findings_by_severity?.[k] ?? 0) > 0).map((k) => {
                  const n = single.scan.findings_by_severity![k]!
                  return (
                    <span
                      key={k}
                      className={`flex flex-col justify-center overflow-hidden whitespace-nowrap px-[10px] bg-badge-${k} text-on-${k}`}
                      style={{ flex: `${Math.max(n, sevTotal(single.scan.findings_by_severity) * 0.07)} 1 0` }}
                    >
                      <span className="tabular-nums text-[14px] font-semibold">{n}</span>
                      <span className="text-[9px] font-semibold tracking-[0.12em]">{SEVERITY_WORD[k].toUpperCase()}</span>
                    </span>
                  )
                })}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-hairline bg-panel shadow-card">
        <div className="flex items-baseline gap-[10px] border-b border-hairline px-[18px] py-[14px]">
          <span className="text-[15px] font-semibold text-ink">Every scan of this {noun}</span>
          <span className="text-[13px] text-muted">
            {history.length} {history.length === 1 ? 'scan' : 'scans'}, newest first
          </span>
        </div>
        {rows.map((item) => (
          <ScanRow
            key={item.id}
            item={item}
            actions={item.kind === 'scan' ? scanActions(item.scan, item.status) : []}
            selected={isHistory && sel?.scan.scan_id === item.id}
            showRepo={false}
            now={now}
          />
        ))}
      </div>
    </div>
  )
}
